import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

interface UseAutoRefreshOptions {
  /** Query key to invalidate. If not provided, invalidates all queries */
  queryKey?: unknown[]
  /** Refresh when tab becomes visible (default: true) */
  refreshOnFocus?: boolean
  /** Refresh on interval in milliseconds (disabled by default) */
  refreshInterval?: number
  /** Whether the auto-refresh is enabled */
  enabled?: boolean
}

/**
 * Provides automatic query refresh functionality similar to react-admin's useRefresh.
 *
 * Features:
 * - Automatically refreshes queries when the browser tab becomes visible
 * - Optional interval-based refresh for real-time updates
 * - Returns a manual refresh function for imperative refresh
 *
 * @example
 * // Auto-refresh conversations when tab becomes visible
 * const refresh = useAutoRefresh({
 *   queryKey: conversationKeys.lists(),
 *   refreshOnFocus: true,
 * })
 *
 * @example
 * // Refresh all queries every 30 seconds
 * useAutoRefresh({
 *   refreshInterval: 30000,
 * })
 *
 * @example
 * // Manual refresh only
 * const refresh = useAutoRefresh({ refreshOnFocus: false })
 * // Later: refresh()
 *
 * @returns refresh function to manually trigger refresh
 */
export function useAutoRefresh({
  queryKey,
  refreshOnFocus = true,
  refreshInterval,
  enabled = true,
}: UseAutoRefreshOptions = {}) {
  const queryClient = useQueryClient()

  const refresh = useCallback(() => {
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey })
    } else {
      queryClient.invalidateQueries()
    }
  }, [queryClient, queryKey])

  // Refresh on visibility change (tab focus)
  useEffect(() => {
    if (!enabled || !refreshOnFocus) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [enabled, refreshOnFocus, refresh])

  // Refresh on interval
  useEffect(() => {
    if (!enabled || !refreshInterval) return

    const intervalId = setInterval(refresh, refreshInterval)
    return () => clearInterval(intervalId)
  }, [enabled, refreshInterval, refresh])

  return refresh
}

/**
 * Simple hook that returns a function to manually refresh queries.
 * Similar to react-admin's useRefresh hook.
 *
 * @param queryKey - Optional: invalidate only queries matching this key
 * @returns refresh function
 *
 * @example
 * const refresh = useRefresh()
 * // Later: refresh() - refreshes all queries
 *
 * @example
 * const refreshConversations = useRefresh(conversationKeys.all)
 * // Later: refreshConversations() - refreshes only conversation queries
 */
export function useRefresh(queryKey?: unknown[]) {
  const queryClient = useQueryClient()

  return useCallback(() => {
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey })
    } else {
      queryClient.invalidateQueries()
    }
  }, [queryClient, queryKey])
}
